import React, {
  ChangeEvent,
  Dispatch,
  SetStateAction,
  useMemo,
  useState,
  useCallback,
} from "react";
import useFetchAllComparisonResearchQueries from "../../../hooks/useFetchAllComparisonResearchQueries";
import { Autocomplete, Chip, MenuItem, TextField } from "@mui/material";
import { CheckCircleRounded } from "@mui/icons-material";

interface IAutoCompleteComparisonResearchQueryId {
  setIsLoading: Dispatch<SetStateAction<boolean>>;
  handelOnChangeComparisonResearchQuery: (
    event: ChangeEvent<any>,
    value: any
  ) => void;
  currentComparisonResearchQueryId:
    | string
    | number
    | null
    | Array<string | number>; // Support both single and multiple values
  sx?: React.CSSProperties;
  className?: string;
  isDisabled?: boolean;
  required?: boolean;
  error?: any;
  helperText?: string;
  listLabel?: string;
  placeholder?: string;
  includeComparisonResearchQueryId?: boolean; // Controls whether to include ID in display
  isMultiple?: boolean; // ✅ NEW: Controls whether multiple selection is enabled
  lazyLoad?: boolean; // ✅ NEW: Controls whether to fetch data on mount or on dropdown open
}

/**
 * All comparison research queries list with auto complete dropdown functionality
 * @returns
 * Selected comparison research query with id and name
 */
const AutoCompleteComparisonResearchQueryId: React.FC<
  IAutoCompleteComparisonResearchQueryId
> = ({
  setIsLoading,
  handelOnChangeComparisonResearchQuery,
  currentComparisonResearchQueryId,
  sx,
  className,
  isDisabled = false,
  required,
  error,
  helperText,
  listLabel,
  placeholder,
  includeComparisonResearchQueryId = false,
  isMultiple = false, // ✅ NEW: Default to false for backward compatibility
  lazyLoad = false, // ✅ NEW: Default to false for backward compatibility
}) => {
  // Track if dropdown has been opened (for lazy loading)
  const [hasOpened, setHasOpened] = useState(false);

  const {
    allComparisonResearchQueries: comparisonResearchQueriesData,
    refetchQueries,
    isLoadingQueries,
  } = useFetchAllComparisonResearchQueries({
    setIsLoading,
    fetchOnMount: !lazyLoad,
  });

  // Handle dropdown open - trigger fetch if lazy loading
  const handleOpen = useCallback(() => {
    if (lazyLoad && !hasOpened) {
      setHasOpened(true);
      refetchQueries();
    }
  }, [lazyLoad, hasOpened, refetchQueries]);

  // Show loading spinner while fetching data
  const isLoading = lazyLoad && (isLoadingQueries || !hasOpened);

  // Compute the value based on single or multiple mode
  const computedValue = useMemo(() => {
    if (!comparisonResearchQueriesData) return isMultiple ? [] : null;

    if (isMultiple) {
      // Handle multiple selection mode
      if (Array.isArray(currentComparisonResearchQueryId)) {
        return comparisonResearchQueriesData.filter((option) =>
          currentComparisonResearchQueryId.includes(option.id)
        );
      }
      // If single value provided in multiple mode, convert to array
      if (currentComparisonResearchQueryId) {
        const found = comparisonResearchQueriesData.find(
          (option) => option.id === currentComparisonResearchQueryId
        );
        return found ? [found] : [];
      }
      return [];
    } else {
      // Handle single selection mode
      return (
        comparisonResearchQueriesData.find(
          (option) => option.id === currentComparisonResearchQueryId
        ) || null
      );
    }
  }, [
    comparisonResearchQueriesData,
    currentComparisonResearchQueryId,
    isMultiple,
  ]);

  // Helper function to get option label
  const getLabel = (option: any) => {
    return includeComparisonResearchQueryId
      ? `(${option.id}) ${option.code}` || ""
      : option.code || "";
  };

  return (
    <Autocomplete
      sx={sx}
      fullWidth={isMultiple}
      multiple={isMultiple}
      disableCloseOnSelect={isMultiple}
      options={comparisonResearchQueriesData || []}
      onOpen={handleOpen}
      loading={isLoading}
      value={computedValue}
      getOptionLabel={getLabel}
      isOptionEqualToValue={(option, value) => option.id === value.id}
      renderInput={(params) =>
        isMultiple ? (
          <div className="autocomplete-chips-direction">
            <TextField
              style={{ color: "#000000" }}
              {...params}
              label={
                <span>
                  {listLabel ? listLabel : "Comparison Research Query"}
                  {required ? <span className="required-asterisk">*</span> : ""}
                </span>
              }
              placeholder={placeholder ? placeholder : "Select..."}
              InputLabelProps={{
                shrink: true,
              }}
              error={!!error}
              helperText={helperText || ""}
            />
          </div>
        ) : (
          <TextField
            style={{ color: "#000000" }}
            {...params}
            label={
              <span>
                {listLabel ? listLabel : "Comparison Research Query"}
                {required ? <span className="required-asterisk">*</span> : ""}
              </span>
            }
            placeholder={placeholder ? placeholder : "Select..."}
            InputLabelProps={{
              shrink: true,
            }}
            error={!!error}
            helperText={helperText || ""}
          />
        )
      }
      renderTags={
        isMultiple
          ? (value, getTagProps) => (
              <div className="align-from-left">
                {value.map((option: any, index: number) => (
                  <Chip
                    {...getTagProps({ index })}
                    key={option.id}
                    label={getLabel(option)}
                  />
                ))}
              </div>
            )
          : undefined
      }
      renderOption={
        isMultiple
          ? (props, option, { selected }) => (
              <MenuItem
                {...props}
                key={`${option?.id}-${option?.code}`}
                value={option?.code}
                sx={{ justifyContent: "space-between" }}
              >
                {getLabel(option)}
                {selected ? <CheckCircleRounded color="info" /> : null}
              </MenuItem>
            )
          : undefined
      }
      className={`${className}${
        isMultiple ? " form-control-autocomplete" : ""
      }`}
      loadingText="Loading..."
      onChange={(event, value) =>
        handelOnChangeComparisonResearchQuery(event, value)
      }
      disabled={Boolean(isDisabled)}
    />
  );
};

export default AutoCompleteComparisonResearchQueryId;
